/**
 * Time-reporting source for the client dashboard's delivery chart.
 *
 * One bar per sprint, stacked by chapter — built from the time actually reported on the product
 * (`/client/products/:id/time`), bucketed into sprints by the API.
 *
 * Hours only. The prototype carries a second, priced series here for its budget view; that is the
 * financial surface this epic does not ship.
 */
import { ClientProductSprintTime } from '@data';
import i18n from '@app/i18n';
import { formatSprintRange, monthLabelsIn } from './sprintRange';
import { ChapterSeries } from './types';

export interface TimeReport {
  /** X-axis label per bar — the month at each month's first sprint, '' in between. */
  periodLabels: string[];
  /** Full tooltip descriptor per bar, e.g. "Sprint 24 · 1–14 Jul". */
  periodTips: string[];
  chapters: ChapterSeries[]; // hours per chapter, per sprint
  hoursSeries: number[]; // total hours per sprint
  totalHours: number;
}

// The window the chart covers: the last 12 months, so the client sees a year of delivery at the
// cadence the team plans in. Whole local days, so the value is stable within a day and doesn't re-key
// the query on every render.
export const timeReportWindow = (today = new Date()): { from: string; to: string } => {
  const from = new Date(today);
  from.setFullYear(from.getFullYear() - 1);
  from.setHours(0, 0, 0, 0);
  const to = new Date(today);
  to.setHours(23, 59, 59, 999);
  return { from: from.toISOString(), to: to.toISOString() };
};

export const sprintRangeLabel = (start: Date, end: Date): string => formatSprintRange(start, end, i18n.language);

// The axis reads as months rather than sprint numbers: only the first sprint of each month is
// labelled (with the year when it changes), so there's one tick per month even though a bar is still
// one sprint. The sprint's number and dates live in the tooltip.
export const monthLabels = (starts: Date[]): string[] => monthLabelsIn(starts, i18n.language);

export const buildTimeReport = (sprints: ClientProductSprintTime[]): TimeReport => {
  // One row per chapter across the whole series, so a chapter that only appears in some sprints still
  // gets a full-length (zero-padded) row and keeps its slot in the stack.
  const labels = new Map<string, string>();
  const hoursByChapter = new Map<string, number[]>();

  const row = (map: Map<string, number[]>, key: string): number[] => {
    const existing = map.get(key);
    if (existing) return existing;
    const created = new Array<number>(sprints.length).fill(0);
    map.set(key, created);
    return created;
  };

  sprints.forEach((sprint, index) => {
    sprint.entries.forEach(entry => {
      const hours = (entry.totalMinutes ?? 0) / 60;
      labels.set(entry.chapterKey, entry.chapterLabel);
      row(hoursByChapter, entry.chapterKey)[index] += hours;
    });
  });

  const keys = [...labels.keys()].sort((a, b) => a.localeCompare(b));
  const series = (values: Map<string, number[]>): ChapterSeries[] =>
    keys.map(key => ({
      key,
      label: labels.get(key) as string,
      values: values.get(key) ?? new Array(sprints.length).fill(0),
    }));

  const chapters = series(hoursByChapter);
  const hoursSeries = sprints.map((_, index) => chapters.reduce((sum, chapter) => sum + chapter.values[index], 0));
  const starts = sprints.map(sprint => new Date(sprint.startDate));

  return {
    periodLabels: monthLabels(starts),
    periodTips: sprints.map((sprint, index) =>
      i18n.t('productDashboard.sprintTip', {
        number: sprint.number,
        range: sprintRangeLabel(starts[index], new Date(sprint.endDate)),
      }),
    ),
    chapters,
    hoursSeries,
    totalHours: hoursSeries.reduce((sum, hours) => sum + hours, 0),
  };
};
